/**
 * Página de Registro de Accidentes para conductores
 * Wizard de 6 pasos para reportar accidentes de forma guiada
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { Capacitor } from '@capacitor/core';
import {
    ArrowLeft,
    ArrowRight,
    AlertTriangle,
    MapPin,
    Bus,
    Car,
    User,
    FileText,
    Camera,
    Image,
    Check,
    Send,
    Plus,
    Trash2,
    Navigation,
    Clock,
    ChevronDown,
    ChevronUp,
    X,
    Save,
    Edit
} from 'lucide-react';
import api from '../services/api';

// Initial form state
const initialFormState = {
    // Step 1: Basic data
    fecha_hora: '',
    lugar: '',
    coordenadas_gps: '',
    numero_bus: '',
    linea: '',

    // Step 2: Incident type
    tipo_vehiculo: false,
    tipo_peaton: false,
    tipo_ciclista: false,
    tipo_mobiliario: false,
    tipo_otros: false,
    danos_bus: '',

    // Step 3: Vehicles (array)
    vehiculos: [],

    // Step 4: Persons (array)
    personas: [],

    // Step 5: Description and photos
    descripcion_detallada: '',
    fotos: [], // File objects

    // Step 6: Confirmation
    enviar_email: false
};

// Empty vehicle template
const emptyVehiculo = {
    titular_dni: '',
    marca: '',
    matricula: '',
    compania_seguros: '',
    numero_poliza: '',
    telefono: '',
    danos: ''
};

// Empty person template
const emptyPersona = {
    nombre_dni: '',
    telefono: '',
    ambulancia: false,
    lesiones: ''
};

export default function Accidentes() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const toast = useToast();

    const [step, setStep] = useState(1);
    const [form, setForm] = useState(initialFormState);
    const [submitting, setSubmitting] = useState(false);
    const [gpsLoading, setGpsLoading] = useState(false);
    const [misPartes, setMisPartes] = useState([]);
    const [showMisPartes, setShowMisPartes] = useState(false);
    const [loadingPartes, setLoadingPartes] = useState(false);
    const [savedAccidenteId, setSavedAccidenteId] = useState(null); // ID of the saved report
    const [emailSending, setEmailSending] = useState(false); // State for email sending
    const [emailSent, setEmailSent] = useState(false); // Flag for successful email
    const [isEditing, setIsEditing] = useState(false); // Mode flag
    const [existingPhotos, setExistingPhotos] = useState([]); // URLs of photos already on server

    const totalSteps = 6;

    // Initialize date/time on mount
    useEffect(() => {
        const now = new Date();
        const fechaHora = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        setForm(prev => ({ ...prev, fecha_hora: fechaHora }));
    }, []);

    // Load previous reports
    useEffect(() => {
        loadMisPartes();
    }, []);

    const loadMisPartes = async () => {
        try {
            setLoadingPartes(true);
            const data = await api.get('/accidentes/mis-partes');
            setMisPartes(data || []);
        } catch (error) {
            console.warn('Error loading partes:', error);
        } finally {
            setLoadingPartes(false);
        }
    };

    // Update form field
    const updateForm = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    // GPS Location
    // GPS Location
    const getAddressFromCoords = async (lat, lon) => {
        try {
            // Usar API de OpenStreetMap (Nominatim)
            // Nota: Respetar política de uso (máximo 1 request por segundo)
            const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`, {
                headers: {
                    'Accept-Language': 'es'
                }
            });
            const data = await response.json();

            if (data && data.address) {
                const addr = data.address;
                const calle = addr.road || addr.street || addr.pedestrian || '';
                const numero = addr.house_number || '';
                const ciudad = addr.city || addr.town || addr.village || 'Vitoria-Gasteiz';

                let direccion = '';
                if (calle) {
                    direccion = `${calle} ${numero}`.trim();
                    if (ciudad) direccion += `, ${ciudad}`;
                } else {
                    direccion = data.display_name;
                }

                if (direccion) {
                    updateForm('lugar', direccion);
                    toast.success('Dirección obtenida: ' + direccion);
                }
            }
        } catch (error) {
            console.warn('Error reverse geocoding:', error);
            // Fallback silencioso, mantenemos las coordenadas
        }
    };

    const obtenerUbicacion = async () => {
        setGpsLoading(true);
        try {
            let lat, lon;

            // Check if running on Capacitor (mobile)
            if (Capacitor.isNativePlatform()) {
                const { Geolocation } = await import('@capacitor/geolocation');
                const position = await Geolocation.getCurrentPosition({
                    enableHighAccuracy: true,
                    timeout: 10000
                });
                lat = position.coords.latitude;
                lon = position.coords.longitude;

                const coords = `${lat.toFixed(6)},${lon.toFixed(6)}`;
                updateForm('coordenadas_gps', coords);
                updateForm('lugar', `Coordenadas: ${coords}`); // Placeholder mientras carga dirección

                await getAddressFromCoords(lat, lon);

            } else {
                // Browser fallback
                if ('geolocation' in navigator) {
                    navigator.geolocation.getCurrentPosition(
                        async (position) => {
                            lat = position.coords.latitude;
                            lon = position.coords.longitude;

                            const coords = `${lat.toFixed(6)},${lon.toFixed(6)}`;
                            updateForm('coordenadas_gps', coords);

                            // Si el campo lugar está vacío o tiene coordenadas antiguas, ponemos las nuevas
                            updateForm('lugar', `Coordenadas: ${coords}`);

                            toast.success('Ubicación GPS obtenida');

                            // Intentar obtener dirección postal
                            await getAddressFromCoords(lat, lon);
                            setGpsLoading(false);
                        },
                        (error) => {
                            console.error('Geolocation error:', error);
                            toast.error('No se pudo obtener la ubicación');
                            setGpsLoading(false); // Ensure loading stops on error callback
                        },
                        { enableHighAccuracy: true, timeout: 10000 }
                    );
                    // Return here to avoid setting loading false immediately in finally block due to async callback
                    // But we can manage it inside callbacks or promises.
                    // To keep it simple in this structure, we'll relying on the callback to call API which is async.
                    // Wait... finally block runs immediately after synchronous part.
                    // We need to handle setGpsLoading(false) inside the callbacks for browser geo.
                    return;
                } else {
                    toast.error('Este navegador no soporta geolocalización');
                    setGpsLoading(false);
                }
            }
        } catch (error) {
            console.error('GPS error:', error);
            toast.error('Error al obtener ubicación');
            setGpsLoading(false);
        }
        // Note: For native, we await, so we can unset loading here.
        // For browser, we returned early.
        if (Capacitor.isNativePlatform()) {
            setGpsLoading(false);
        }
    };

    // Navigation
    const nextStep = () => {
        // Validate current step
        if (step === 1) {
            if (!form.numero_bus.trim()) {
                toast.error('Introduce el número de autobús');
                return;
            }
        }
        if (step === 2) {
            if (!form.tipo_vehiculo && !form.tipo_peaton && !form.tipo_ciclista && !form.tipo_mobiliario && !form.tipo_otros) {
                toast.error('Selecciona al menos un tipo de incidente');
                return;
            }
        }

        // Skip vehicle step if not selected
        if (step === 2 && !form.tipo_vehiculo) {
            if (!form.tipo_peaton && !form.tipo_ciclista) {
                // Skip to step 5 if no people either
                setStep(5);
                return;
            }
            // Skip to step 4 (persons)
            setStep(4);
            return;
        }

        // Skip persons step if not selected
        if (step === 3 && !form.tipo_peaton && !form.tipo_ciclista) {
            setStep(5);
            return;
        }

        setStep(prev => Math.min(prev + 1, totalSteps));
    };

    const prevStep = () => {
        // Handle going back with skipped steps
        if (step === 5 && !form.tipo_peaton && !form.tipo_ciclista && !form.tipo_vehiculo) {
            setStep(2);
            return;
        }
        if (step === 5 && !form.tipo_peaton && !form.tipo_ciclista) {
            setStep(3);
            return;
        }
        if (step === 4 && !form.tipo_vehiculo) {
            setStep(2);
            return;
        }
        setStep(prev => Math.max(prev - 1, 1));
    };

    // Vehicles management
    const addVehiculo = () => {
        setForm(prev => ({
            ...prev,
            vehiculos: [...prev.vehiculos, { ...emptyVehiculo }]
        }));
    };

    const updateVehiculo = (index, field, value) => {
        setForm(prev => ({
            ...prev,
            vehiculos: prev.vehiculos.map((v, i) =>
                i === index ? { ...v, [field]: value } : v
            )
        }));
    };

    const removeVehiculo = (index) => {
        setForm(prev => ({
            ...prev,
            vehiculos: prev.vehiculos.filter((_, i) => i !== index)
        }));
    };

    // Persons management
    const addPersona = () => {
        setForm(prev => ({
            ...prev,
            personas: [...prev.personas, { ...emptyPersona }]
        }));
    };

    const updatePersona = (index, field, value) => {
        setForm(prev => ({
            ...prev,
            personas: prev.personas.map((p, i) =>
                i === index ? { ...p, [field]: value } : p
            )
        }));
    };

    const removePersona = (index) => {
        setForm(prev => ({
            ...prev,
            personas: prev.personas.filter((_, i) => i !== index)
        }));
    };

    // Photo handling
    const handlePhotoCapture = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length + form.fotos.length > 10) {
            toast.error('Máximo 10 fotos');
            return;
        }
        setForm(prev => ({
            ...prev,
            fotos: [...prev.fotos, ...files]
        }));
    };

    const removePhoto = (index) => {
        setForm(prev => ({
            ...prev,
            fotos: prev.fotos.filter((_, i) => i !== index)
        }));
    };

    // Submit - Save or Update
    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            let response;
            let accidenteId;

            if (isEditing && savedAccidenteId) {
                // UPDATE existing report
                response = await api.put(`/accidentes/${savedAccidenteId}`, {
                    fecha_hora: form.fecha_hora,
                    lugar: form.lugar,
                    coordenadas_gps: form.coordenadas_gps,
                    numero_bus: form.numero_bus,
                    linea: form.linea,
                    danos_bus: form.danos_bus,
                    tipo_vehiculo: form.tipo_vehiculo,
                    tipo_peaton: form.tipo_peaton,
                    tipo_ciclista: form.tipo_ciclista,
                    tipo_mobiliario: form.tipo_mobiliario,
                    tipo_otros: form.tipo_otros,
                    descripcion_detallada: form.descripcion_detallada,
                    vehiculos: form.vehiculos,
                    personas: form.personas
                });
                accidenteId = savedAccidenteId;
                toast.success(`Parte actualizado correctamente`);
            } else {
                // CREATE new report
                response = await api.post('/accidentes', {
                    fecha_hora: form.fecha_hora,
                    lugar: form.lugar,
                    coordenadas_gps: form.coordenadas_gps,
                    numero_bus: form.numero_bus,
                    linea: form.linea,
                    danos_bus: form.danos_bus,
                    tipo_vehiculo: form.tipo_vehiculo,
                    tipo_peaton: form.tipo_peaton,
                    tipo_ciclista: form.tipo_ciclista,
                    tipo_mobiliario: form.tipo_mobiliario,
                    tipo_otros: form.tipo_otros,
                    descripcion_detallada: form.descripcion_detallada,
                    vehiculos: form.vehiculos,
                    personas: form.personas
                });
                accidenteId = response.id;
                toast.success(`Parte ${response.numero_parte} guardado correctamente`);
            }

            // Upload NEW photos if any
            // Note: form.fotos contains File objects (new)
            // Existing photos are in existingPhotos state (URLs)
            if (form.fotos.length > 0 && accidenteId) {
                const formData = new FormData();
                form.fotos.forEach(foto => {
                    formData.append('fotos', foto);
                });

                try {
                    await api.postFormData(`/accidentes/${accidenteId}/fotos`, formData);
                    // Clear new photos from form after upload as they are now saved
                    setForm(prev => ({ ...prev, fotos: [] }));
                    toast.success('Nuevas fotos subidas correctamente');
                } catch (photoError) {
                    console.warn('Error uploading photos:', photoError);
                    toast.warning('Datos guardados pero hubo un error con las fotos');
                }
            }

            // Set saved state to show next actions
            setSavedAccidenteId(accidenteId);
            setIsEditing(true); // Now we are effectively editing the saved one
            loadMisPartes(); // Refresh list immediately

        } catch (error) {
            console.error('Error submitting:', error);
            toast.error(error.message || 'Error al guardar el parte');
        } finally {
            setSubmitting(false);
        }
    };

    // Manual Email Trigger
    const handleSendEmail = async () => {
        if (!savedAccidenteId) return;

        setEmailSending(true);
        try {
            await api.post(`/accidentes/${savedAccidenteId}/email`);
            toast.success('Notificación enviada correctamente a administración');
            setEmailSent(true);
        } catch (error) {
            console.error('Email error:', error);
            toast.error('Error enviando email: ' + (error.message || 'Error desconocido'));
        } finally {
            setEmailSending(false);
        }
    };

    // Finish and exit
    const handleFinish = () => {
        setForm(initialFormState);
        setStep(1);
        setSavedAccidenteId(null);
        setEmailSent(false);
        setIsEditing(false);
        setExistingPhotos([]);
        navigate('/user');
    };

    // Edit Handler
    const handleEdit = async (parteId) => {
        try {
            setLoadingPartes(true);
            const data = await api.get(`/accidentes/${parteId}`);

            // Map API data to form state
            setForm({
                fecha_hora: data.fecha_hora_fmt ? data.fecha_hora_fmt.replace(' ', 'T') : '', // Format for datetime-local
                lugar: data.lugar || '',
                coordenadas_gps: data.coordenadas_gps || '',
                numero_bus: data.numero_bus || '',
                linea: data.linea || '',
                danos_bus: data.danos_bus || '',
                tipo_vehiculo: !!data.tipo_vehiculo,
                tipo_peaton: !!data.tipo_peaton,
                tipo_ciclista: !!data.tipo_ciclista,
                tipo_mobiliario: !!data.tipo_mobiliario,
                tipo_otros: !!data.tipo_otros,
                descripcion_detallada: data.descripcion_detallada || '',
                vehiculos: data.vehiculos || [],
                personas: data.personas || [],
                fotos: [] // New photos start empty
            });

            setExistingPhotos(data.fotos || []);
            setSavedAccidenteId(parteId);
            setIsEditing(true);
            setEmailSent(!!data.enviado_email);
            setStep(1); // Go to first step to review

            // Scroll to top
            window.scrollTo(0, 0);

            // Hide list
            setShowMisPartes(false);

        } catch (error) {
            console.error('Error loading report:', error);
            toast.error('Error al cargar datos del parte');
        } finally {
            setLoadingPartes(false);
        }
    };

    // Step indicator
    const StepIndicator = () => (
        <div className="flex items-center justify-center gap-1 mb-4">
            {[1, 2, 3, 4, 5, 6].map(s => (
                <div
                    key={s}
                    className={`w-2 h-2 rounded-full transition-all ${s === step
                        ? 'w-6 bg-red-500'
                        : s < step
                            ? 'bg-green-500'
                            : 'bg-white/20'
                        }`}
                />
            ))}
        </div>
    );

    // Render step content
    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-red-400" />
                            Datos del Accidente
                        </h2>

                        {/* Fecha y Hora */}
                        <div>
                            <label className="flex items-center gap-2 text-sm text-[var(--text-muted)] mb-2">
                                <Clock className="w-4 h-4" />
                                Fecha y Hora
                            </label>
                            <input
                                type="datetime-local"
                                value={form.fecha_hora}
                                onChange={(e) => updateForm('fecha_hora', e.target.value)}
                                className="w-full px-4 py-3 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-xl text-[var(--text-primary)] focus:outline-none focus:border-red-500/50"
                            />
                        </div>

                        {/* Lugar */}
                        <div>
                            <label className="flex items-center gap-2 text-sm text-[var(--text-muted)] mb-2">
                                <MapPin className="w-4 h-4" />
                                Lugar del Accidente
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={form.lugar}
                                    onChange={(e) => updateForm('lugar', e.target.value)}
                                    placeholder="Ej: Calle Portal de Foronda, 12"
                                    className="flex-1 px-4 py-3 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-xl text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-red-500/50"
                                />
                                <button
                                    type="button"
                                    onClick={obtenerUbicacion}
                                    disabled={gpsLoading}
                                    className="px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl text-white flex items-center gap-2 disabled:opacity-50"
                                >
                                    {gpsLoading ? (
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <Navigation className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                            {form.coordenadas_gps && (
                                <p className="text-xs text-green-400 mt-1">
                                    📍 GPS: {form.coordenadas_gps}
                                </p>
                            )}
                        </div>

                        {/* Nº Bus */}
                        <div>
                            <label className="flex items-center gap-2 text-sm text-[var(--text-muted)] mb-2">
                                <Bus className="w-4 h-4" />
                                Nº Autobús *
                            </label>
                            <input
                                type="text"
                                value={form.numero_bus}
                                onChange={(e) => updateForm('numero_bus', e.target.value)}
                                placeholder="Ej: 142"
                                className="w-full px-4 py-3 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-xl text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-red-500/50"
                            />
                        </div>

                        {/* Línea */}
                        <div>
                            <label className="flex items-center gap-2 text-sm text-[var(--text-muted)] mb-2">
                                Línea / Servidor
                            </label>
                            <input
                                type="text"
                                value={form.linea}
                                onChange={(e) => updateForm('linea', e.target.value)}
                                placeholder="Ej: L5 o Servidor 23"
                                className="w-full px-4 py-3 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-xl text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-red-500/50"
                            />
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-yellow-400" />
                            ¿Qué ocurrió?
                        </h2>
                        <p className="text-sm text-[var(--text-muted)]">Selecciona todo lo que aplique:</p>

                        {/* Type chips */}
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { key: 'tipo_vehiculo', icon: Car, label: 'Vehículo' },
                                { key: 'tipo_peaton', icon: User, label: 'Peatón' },
                                { key: 'tipo_ciclista', icon: () => <span>🚴</span>, label: 'Ciclista' },
                                { key: 'tipo_mobiliario', icon: () => <span>🪑</span>, label: 'Mobiliario' },
                                { key: 'tipo_otros', icon: () => <span>📦</span>, label: 'Otros' },
                            ].map(({ key, icon: Icon, label }) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => updateForm(key, !form[key])}
                                    className={`flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${form[key]
                                        ? 'bg-red-500/20 border-red-500 text-white'
                                        : 'bg-white/5 border-white/10 text-[var(--text-muted)] hover:bg-white/10'
                                        }`}
                                >
                                    <Icon className="w-6 h-6" />
                                    <span className="font-medium">{label}</span>
                                </button>
                            ))}
                        </div>

                        {/* Daños al bus */}
                        <div>
                            <label className="flex items-center gap-2 text-sm text-[var(--text-muted)] mb-2">
                                <Bus className="w-4 h-4" />
                                Daños al autobús
                            </label>
                            <textarea
                                value={form.danos_bus}
                                onChange={(e) => updateForm('danos_bus', e.target.value)}
                                placeholder="Describe los daños visibles en el autobús..."
                                rows={3}
                                className="w-full px-4 py-3 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-xl text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-red-500/50 resize-none"
                            />
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <Car className="w-5 h-5 text-blue-400" />
                            Vehículo Contrario
                        </h2>

                        {form.vehiculos.length === 0 && (
                            <p className="text-sm text-[var(--text-muted)]">
                                Añade los datos del vehículo implicado
                            </p>
                        )}

                        {form.vehiculos.map((v, idx) => (
                            <div key={idx} className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-300">Vehículo {idx + 1}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeVehiculo(idx)}
                                        className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>

                                <input
                                    type="text"
                                    value={v.titular_dni}
                                    onChange={(e) => updateVehiculo(idx, 'titular_dni', e.target.value)}
                                    placeholder="Titular / DNI"
                                    className="w-full px-3 py-2.5 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-blue-500/50 text-sm"
                                />

                                <div className="grid grid-cols-2 gap-2">
                                    <input
                                        type="text"
                                        value={v.marca}
                                        onChange={(e) => updateVehiculo(idx, 'marca', e.target.value)}
                                        placeholder="Marca"
                                        className="px-3 py-2.5 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-blue-500/50 text-sm"
                                    />
                                    <input
                                        type="text"
                                        value={v.matricula}
                                        onChange={(e) => updateVehiculo(idx, 'matricula', e.target.value.toUpperCase())}
                                        placeholder="Matrícula"
                                        className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 text-sm uppercase"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <input
                                        type="text"
                                        value={v.compania_seguros}
                                        onChange={(e) => updateVehiculo(idx, 'compania_seguros', e.target.value)}
                                        placeholder="Cía. Seguros"
                                        className="px-3 py-2.5 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-blue-500/50 text-sm"
                                    />
                                    <input
                                        type="text"
                                        value={v.numero_poliza}
                                        onChange={(e) => updateVehiculo(idx, 'numero_poliza', e.target.value)}
                                        placeholder="Nº Póliza"
                                        className="px-3 py-2.5 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-blue-500/50 text-sm"
                                    />
                                </div>

                                <input
                                    type="tel"
                                    value={v.telefono}
                                    onChange={(e) => updateVehiculo(idx, 'telefono', e.target.value)}
                                    placeholder="Teléfono"
                                    className="w-full px-3 py-2.5 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-blue-500/50 text-sm"
                                />

                                <textarea
                                    value={v.danos}
                                    onChange={(e) => updateVehiculo(idx, 'danos', e.target.value)}
                                    placeholder="Daños del vehículo..."
                                    rows={2}
                                    className="w-full px-3 py-2.5 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-blue-500/50 text-sm resize-none"
                                />
                            </div>
                        ))}

                        <button
                            type="button"
                            onClick={addVehiculo}
                            className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-white/20 rounded-xl text-[var(--text-muted)] hover:border-white/40 hover:text-white transition-all"
                        >
                            <Plus className="w-5 h-5" />
                            Añadir vehículo
                        </button>
                    </div>
                );

            case 4:
                return (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <User className="w-5 h-5 text-purple-400" />
                            Personas Afectadas
                        </h2>

                        {form.personas.length === 0 && (
                            <p className="text-sm text-[var(--text-muted)]">
                                Añade los datos de las personas afectadas
                            </p>
                        )}

                        {form.personas.map((p, idx) => (
                            <div key={idx} className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-300">Persona {idx + 1}</span>
                                    <button
                                        type="button"
                                        onClick={() => removePersona(idx)}
                                        className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>

                                <input
                                    type="text"
                                    value={p.nombre_dni}
                                    onChange={(e) => updatePersona(idx, 'nombre_dni', e.target.value)}
                                    placeholder="Nombre y DNI"
                                    className="w-full px-3 py-2.5 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-purple-500/50 text-sm"
                                />

                                <input
                                    type="tel"
                                    value={p.telefono}
                                    onChange={(e) => updatePersona(idx, 'telefono', e.target.value)}
                                    placeholder="Teléfono"
                                    className="w-full px-3 py-2.5 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-purple-500/50 text-sm"
                                />

                                {/* Ambulancia toggle */}
                                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                    <span className="text-sm text-gray-300">¿Ambulancia?</span>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => updatePersona(idx, 'ambulancia', true)}
                                            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${p.ambulancia
                                                ? 'bg-red-500 text-white'
                                                : 'bg-white/10 text-[var(--text-muted)]'
                                                }`}
                                        >
                                            Sí
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => updatePersona(idx, 'ambulancia', false)}
                                            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${!p.ambulancia
                                                ? 'bg-green-500 text-white'
                                                : 'bg-white/10 text-[var(--text-muted)]'
                                                }`}
                                        >
                                            No
                                        </button>
                                    </div>
                                </div>

                                <textarea
                                    value={p.lesiones}
                                    onChange={(e) => updatePersona(idx, 'lesiones', e.target.value)}
                                    placeholder="Describe las lesiones..."
                                    rows={2}
                                    className="w-full px-3 py-2.5 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-purple-500/50 text-sm resize-none"
                                />
                            </div>
                        ))}

                        <button
                            type="button"
                            onClick={addPersona}
                            className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-white/20 rounded-xl text-[var(--text-muted)] hover:border-white/40 hover:text-white transition-all"
                        >
                            <Plus className="w-5 h-5" />
                            Añadir persona afectada
                        </button>
                    </div>
                );

            case 5:
                return (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <FileText className="w-5 h-5 text-green-400" />
                            Descripción y Fotos
                        </h2>

                        {/* Descripción */}
                        <div>
                            <label className="text-sm text-[var(--text-muted)] mb-2 block">
                                Descripción detallada del accidente
                            </label>
                            <textarea
                                value={form.descripcion_detallada}
                                onChange={(e) => updateForm('descripcion_detallada', e.target.value)}
                                placeholder="Describe cómo ocurrió el accidente con el mayor detalle posible: velocidad aproximada, maniobras, condiciones de la vía..."
                                rows={6}
                                className="w-full px-4 py-3 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-xl text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-green-500/50 resize-none"
                            />
                        </div>
                        <div className="space-y-4">
                            <h2 className="text-lg font-semibold flex items-center gap-2">
                                <Camera className="w-5 h-5 text-purple-400" />
                                Fotos del Accidente
                            </h2>

                            <div className="bg-white/5 rounded-xl p-4 space-y-4">
                                {/* Existing Photos (Read Only in UI) */}
                                {existingPhotos.length > 0 && (
                                    <div className="mb-4">
                                        <p className="text-sm text-[var(--text-muted)] mb-2">Fotos guardadas ({existingPhotos.length}):</p>
                                        <div className="flex flex-wrap gap-2">
                                            {existingPhotos.map((foto) => {
                                                const uri = api.getUri();
                                                // Handle potential double slashes if API_BASE handles trailing slash differntly,
                                                // though getUri strips /api.
                                                // foto.ruta_archivo already starts with /uploads/...
                                                const imgSrc = `${uri}${foto.ruta_archivo}`;

                                                return (
                                                    <div key={foto.id} className="relative w-24 h-24 rounded-lg overflow-hidden border border-white/20">
                                                        <img
                                                            src={imgSrc}
                                                            alt="Foto guardada"
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = '/placeholder-image.png'; // Fallback if available, or just keep broken icon
                                                                console.error('Error loading image:', imgSrc);
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-3">
                                    {/* New Photos Preview */}
                                    {form.fotos.map((foto, idx) => (
                                        <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-white/20">
                                            <img
                                                src={URL.createObjectURL(foto)}
                                                alt={`Nueva ${idx}`}
                                                className="w-full h-full object-cover"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removePhoto(idx)}
                                                className="absolute top-1 right-1 p-1 bg-red-500 rounded-full"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}

                                    {/* Add photo buttons */}
                                    {form.fotos.length < 10 && (
                                        <>
                                            <label className="w-20 h-20 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-white/40 transition-all">
                                                <Camera className="w-6 h-6 text-[var(--text-muted)]" />
                                                <span className="text-xs text-[var(--text-secondary)]">Cámara</span>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    capture="environment"
                                                    onChange={handlePhotoCapture}
                                                    className="hidden"
                                                />
                                            </label>

                                            <label className="w-20 h-20 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-white/40 transition-all">
                                                <Image className="w-6 h-6 text-[var(--text-muted)]" />
                                                <span className="text-xs text-[var(--text-secondary)]">Galería</span>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    onChange={handlePhotoCapture}
                                                    className="hidden"
                                                />
                                            </label>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 6:
                return (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <Check className="w-5 h-5 text-green-400" />
                            Confirmar y Enviar
                        </h2>

                        {/* Summary */}
                        <div className="bg-white/5 rounded-xl p-4 space-y-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-[var(--text-muted)]">Fecha/Hora:</span>
                                <span>{form.fecha_hora?.replace('T', ' ')}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[var(--text-muted)]">Lugar:</span>
                                <span className="text-right max-w-[60%]">{form.lugar || 'No especificado'}</span>
                            </div>
                            {form.coordenadas_gps && (
                                <div className="flex justify-between">
                                    <span className="text-[var(--text-muted)]">GPS:</span>
                                    <span className="text-green-400">✓ Capturado</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-gray-400">Autobús:</span>
                                <span>{form.numero_bus} {form.linea && `(${form.linea})`}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Tipo:</span>
                                <span>
                                    {[
                                        form.tipo_vehiculo && 'Vehículo',
                                        form.tipo_peaton && 'Peatón',
                                        form.tipo_ciclista && 'Ciclista',
                                        form.tipo_mobiliario && 'Mobiliario',
                                        form.tipo_otros && 'Otros'
                                    ].filter(Boolean).join(', ')}
                                </span>
                            </div>
                            {form.vehiculos.length > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Vehículos:</span>
                                    <span>{form.vehiculos.length}</span>
                                </div>
                            )}
                            {form.personas.length > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Personas afectadas:</span>
                                    <span>{form.personas.length}</span>
                                </div>
                            )}
                            {form.fotos.length > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Fotos:</span>
                                    <span>{form.fotos.length}</span>
                                </div>
                            )}
                        </div>

                        {/* Description preview */}
                        {form.descripcion_detallada && (
                            <div className="bg-white/5 rounded-xl p-4">
                                <p className="text-xs text-gray-400 mb-1">Descripción:</p>
                                <p className="text-sm line-clamp-3">{form.descripcion_detallada}</p>
                            </div>
                        )}

                        {/* Email option - future implementation */}
                        {/* <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                            <span className="text-sm">Enviar copia por email</span>
                            <input
                                type="checkbox"
                                checked={form.enviar_email}
                                onChange={(e) => updateForm('enviar_email', e.target.checked)}
                                className="w-5 h-5 rounded"
                            />
                        </div> */}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="space-y-4 pb-24">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-orange-600 px-4 py-6 rounded-b-3xl shadow-lg relative mb-4">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => step > 1 ? prevStep() : navigate('/user')}
                        className="bg-white/20 hover:bg-white/30 backdrop-blur p-2 rounded-full transition-colors text-white"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold text-white">Parte de Accidente</h1>
                        <p className="text-sm font-medium text-red-100">Paso {step} de {totalSteps}</p>
                    </div>
                </div>
            </div>

            {/* Step Indicator */}
            <StepIndicator />

            {/* Form Card */}
            <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] shadow-sm rounded-2xl p-4 mx-4">
                {renderStep()}
            </div>

            {/* Navigation Buttons */}
            <div className="flex gap-3 px-4 mt-4">
                {step > 1 && (
                    <button
                        type="button"
                        onClick={prevStep}
                        className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl font-bold text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-all"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Anterior
                    </button>
                )}

                {step < totalSteps ? (
                    <button
                        type="button"
                        onClick={nextStep}
                        className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl font-bold text-white shadow-lg shadow-red-500/30 transition-transform active:scale-95"
                    >
                        Siguiente
                        <ArrowRight className="w-5 h-5" />
                    </button>
                ) : (
                    // Step 6 Buttons Logic
                    !savedAccidenteId ? (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl font-medium text-white disabled:opacity-50 hover:shadow-lg hover:shadow-blue-500/30 transition-all"
                        >
                            {submitting ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <Save className="w-5 h-5" />
                                    Guardar Parte
                                </>
                            )}
                        </button>
                    ) : (
                        <div className="flex flex-col gap-2 w-full">
                            {/* Email Button */}
                            {!emailSent ? (
                                <button
                                    type="button"
                                    onClick={handleSendEmail}
                                    disabled={emailSending}
                                    className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl font-medium text-white disabled:opacity-50 hover:shadow-lg hover:shadow-green-500/30 transition-all"
                                >
                                    {emailSending ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Enviando Email...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-5 h-5" />
                                            Enviar Notificación Email
                                        </>
                                    )}
                                </button>
                            ) : (
                                <div className="p-3 bg-green-500/20 border border-green-500/50 rounded-xl text-center text-green-400 font-medium">
                                    ✓ Email enviado correctamente
                                </div>
                            )}

                            {/* Finish Button */}
                            <button
                                type="button"
                                onClick={handleFinish}
                                className="w-full py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white font-medium transition-colors"
                            >
                                Finalizar y Salir
                            </button>
                        </div>
                    )
                )}
            </div>

            {/* Previous Reports Section */}
            <div className="mt-6">
                <button
                    type="button"
                    onClick={() => setShowMisPartes(!showMisPartes)}
                    className="w-full flex items-center justify-between p-4 bg-white/5 rounded-xl text-sm"
                >
                    <span className="text-gray-400">Mis partes anteriores ({misPartes.length})</span>
                    {showMisPartes ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {showMisPartes && (
                    <div className="mt-2 space-y-2">
                        {loadingPartes ? (
                            <div className="flex justify-center py-4">
                                <div className="w-6 h-6 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : misPartes.length === 0 ? (
                            <p className="text-center py-4 text-gray-500 text-sm">
                                No tienes partes anteriores
                            </p>
                        ) : (
                            misPartes.map(parte => (
                                <div key={parte.id} className="bg-white/5 rounded-xl p-3 border border-white/10">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="font-medium text-sm">{parte.numero_parte}</p>
                                            <p className="text-xs text-gray-400">{parte.fecha_hora}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs px-2 py-1 bg-white/10 rounded">
                                                Bus {parte.numero_bus}
                                            </span>
                                            <button
                                                onClick={() => handleEdit(parte.id)}
                                                className="p-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    {parte.lugar && (
                                        <p className="text-xs text-gray-500 mt-1 truncate">{parte.lugar}</p>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
